// Contracting institution logo (BLD-25): upload in Settings, shown in the sidebar and on PDF headers.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ImageUp, Trash2 } from "lucide-react";
import { Button, Panel } from "./ui.tsx";
import { useToast } from "./toast.tsx";
import { post, put } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import type { PlanDetail } from "../lib/types.ts";

export const logoUrl = (plan: Pick<PlanDetail, "id" | "institution">) =>
  plan.institution.logo_version ? `/api/plans/${plan.id}/institution/logo?v=${plan.institution.logo_version}` : null;

/**
 * Turns a near-white or light-gray background transparent (logos often come as JPEG on a light background) and
 * returns a PNG. Pixels are faded by how close they are to the background colour sampled at the corners.
 */
async function clearBackground(file: File): Promise<Blob> {
  const img = await createImageBitmap(file);
  const scale = Math.min(1, 1200 / img.width);
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * scale);
  c.height = Math.round(img.height * scale);
  const x = c.getContext("2d")!;
  x.drawImage(img, 0, 0, c.width, c.height);
  const im = x.getImageData(0, 0, c.width, c.height);
  const d = im.data;
  const at = (px: number, py: number) => { const i = (py * c.width + px) * 4; return [d[i]!, d[i + 1]!, d[i + 2]!]; };
  const corners = [at(1, 1), at(c.width - 2, 1), at(1, c.height - 2), at(c.width - 2, c.height - 2)];
  const bg = [0, 1, 2].map((k) => corners.reduce((s, p) => s + p[k]!, 0) / 4);
  // Only light backgrounds are removed; a dark or coloured background is kept as it is.
  if (Math.min(...bg) > 200) {
    for (let i = 0; i < d.length; i += 4) {
      const dist = Math.hypot(d[i]! - bg[0]!, d[i + 1]! - bg[1]!, d[i + 2]! - bg[2]!);
      const a = Math.max(0, Math.min(1, (dist - 10) / 40));
      d[i + 3] = Math.round(d[i + 3]! * a);
    }
    x.putImageData(im, 0, 0);
  }
  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error("Falha ao processar a imagem."))), "image/png"));
}

const toBase64 = async (b: Blob) => {
  const bytes = new Uint8Array(await b.arrayBuffer());
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
};

export function InstitutionLogoPanel() {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [clear, setClear] = useState(true);
  const url = logoUrl(plan);

  async function apply(next: PlanDetail) {
    qc.setQueryData(planKey(plan.id, "detail"), next);
    await qc.invalidateQueries({ queryKey: ["plans"] });
  }
  async function upload(file: File | undefined) {
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) return toast("danger", "Envie uma imagem PNG, JPEG ou WebP.");
    setBusy(true);
    try {
      const blob = clear ? await clearBackground(file) : file;
      if (blob.size > 1_048_576) throw new Error("A imagem pode ter no máximo 1 MB.");
      const res = await put<{ data: PlanDetail }>(`/plans/${plan.id}/institution/logo`, { data: await toBase64(blob) });
      await apply(res.data);
      toast("success", "Logomarca atualizada.");
    } catch (e) {
      toast("danger", e instanceof Error ? e.message : "Não foi possível enviar a imagem.");
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    try {
      await apply((await post<{ data: PlanDetail }>(`/plans/${plan.id}/institution/logo/remove`, {})).data);
      toast("success", "Logomarca removida.");
    } catch (e) {
      toast("danger", e instanceof Error ? e.message : "Não foi possível remover.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel className="lg:col-span-2">
      <h2 className="text-lg font-bold">Logomarca da instituição</h2>
      <p className="mt-1 text-sm text-muted">
        Aparece no menu lateral e no cabeçalho dos relatórios em PDF, ao lado da Trilhar. Vale para todas as oficinas de {plan.institution.name}.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-5">
        <div className="flex h-20 w-64 items-center justify-center rounded-ctl border border-dashed border-field bg-paper p-2">
          {url ? <img src={url} alt={`Logomarca de ${plan.institution.name}`} className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-ink-3">Sem logomarca</span>}
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 accent-primary" checked={clear} onChange={(e) => setClear(e.target.checked)} />
            Tornar o fundo claro transparente
          </label>
          <div className="flex flex-wrap gap-2">
            <label className={`inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-ctl border border-primary bg-primary px-4 text-sm font-semibold text-white shadow-e1 transition-[background-color,box-shadow] hover:bg-primary-dark hover:shadow-e2 ${busy ? "pointer-events-none opacity-60" : ""}`}>
              <ImageUp className="size-4" aria-hidden />{url ? "Trocar imagem" : "Enviar imagem"}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" disabled={busy}
                onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {url && <Button variant="dangerGhost" disabled={busy} onClick={() => void remove()}><Trash2 className="size-4" aria-hidden />Remover</Button>}
          </div>
          <p className="text-xs text-ink-3">PNG, JPEG ou WebP, até 1 MB.</p>
        </div>
      </div>
    </Panel>
  );
}
